import * as THREE from 'three';
import {ViewOrientationMaterialFactory} from '../../material/ViewOrientationMaterialFactory';
import {getStandardViewDefinition} from './standardViewDefinitions';
import {StandardViewType, ViewOrientationTargetKind} from './standardViewTypes';
import {ViewOrientationElementMesh} from './ViewOrientationElementMesh';

/**
 * 角点网格。
 *
 * 设计说明：
 * 1. 该类一次只生成一个角点面，禁止在内部批量创建 8 个角点；
 * 2. 角点面直接根据当前主面与连接面的闭合顶点生成，避免再走旧的球体近似；
 * 3. 通过继承 ViewOrientationElementMesh，继续保留命中语义和交互链兼容性。
 */
export class ViewOrientationCornerMesh extends ViewOrientationElementMesh {

    /**
   * 构造单个角点 mesh。
   *
   * @param viewType 标准视图类型。
   * @throws 当视图定义不是角点类型时抛出错误。
   */
  public constructor(viewType: StandardViewType) {
    const definition = getStandardViewDefinition(viewType);
    if (definition.kind !== ViewOrientationTargetKind.Corner) {
      throw new Error(`角点类型错误：${viewType}`);
    }

    const metrics = ViewOrientationCornerMesh.createMetrics();
    const transform = ViewOrientationCornerMesh.createCornerTransform(viewType, metrics);
    const material = ViewOrientationMaterialFactory.createBasicMaterial(definition.kind);

    super({
      viewType,
      kind: definition.kind,
      label: definition.label,
      geometry: transform.geometry,
      material,
      renderOrder: ViewOrientationElementMesh.RENDER_ORDER,
    });

    this.name = `view-orientation-corner-${viewType}`;
    this.applyCornerPlacement(transform.position, transform.quaternion);
  }

  /**
   * 释放资源。
   */
  public dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }

  /**
   * 创建角点尺寸参数。
   *
   * @returns 尺寸参数。
   */
  private static createMetrics(): {
    outerHalfSize: number;
    edgeCut: number;
  } {
    const size = ViewOrientationElementMesh.SIZE;
    const outerHalfSize = size / 2;
    const edgeCut = size / (4 + 4 * Math.SQRT2);

    return {
      outerHalfSize,
      edgeCut,
    };
  }

  /**
   * 创建角点六边形几何与姿态。
   *
   * @param viewType 标准视图类型。
   * @param metrics 尺寸参数。
   * @returns 角点几何与姿态。
   */
  private static createCornerTransform(
    viewType: StandardViewType,
    metrics: {
      outerHalfSize: number;
      edgeCut: number;
    },
  ): {
    geometry: THREE.ShapeGeometry;
    position: THREE.Vector3;
    quaternion: THREE.Quaternion;
  } {
    const worldVertices = ViewOrientationCornerMesh.createCornerWorldVertices(viewType, metrics);
    const centroid = worldVertices.reduce((sum, vertex) => sum.add(vertex.clone()), new THREE.Vector3())
      .multiplyScalar(1 / worldVertices.length);
    const normalAxis = centroid.clone().normalize();
    const widthAxis = worldVertices[0].clone().sub(centroid).normalize();
    const heightAxis = new THREE.Vector3().crossVectors(normalAxis, widthAxis).normalize();
    const shape = new THREE.Shape();

    worldVertices.forEach((vertex, index) => {
      const localVertex = vertex.clone().sub(centroid);
      const x = localVertex.dot(widthAxis);
      const y = localVertex.dot(heightAxis);

      if (index === 0) {
        shape.moveTo(x, y);
        return;
      }

      shape.lineTo(x, y);
    });
    shape.closePath();

    const geometry = new THREE.ShapeGeometry(shape);
    const rotationMatrix = new THREE.Matrix4().makeBasis(widthAxis, heightAxis, normalAxis);
    const quaternion = new THREE.Quaternion().setFromRotationMatrix(rotationMatrix);

    return {
      geometry,
      position: centroid,
      quaternion,
    };
  }

  /**
   * 生成角点面的世界顶点。
   *
   * @param viewType 标准视图类型。
   * @param metrics 尺寸参数。
   * @returns 世界顶点序列。
   */
  private static createCornerWorldVertices(
    viewType: StandardViewType,
    metrics: {
      outerHalfSize: number;
      edgeCut: number;
    },
  ): readonly THREE.Vector3[] {
    const signs = ViewOrientationCornerMesh.resolveCornerSigns(viewType);
    const outerValue = metrics.outerHalfSize;
    const innerValue = metrics.outerHalfSize - metrics.edgeCut;
    const bridgeValue = metrics.outerHalfSize - metrics.edgeCut * 2;

    return [
      new THREE.Vector3(bridgeValue * signs[0], innerValue * signs[1], outerValue * signs[2]),
      new THREE.Vector3(innerValue * signs[0], bridgeValue * signs[1], outerValue * signs[2]),
      new THREE.Vector3(outerValue * signs[0], bridgeValue * signs[1], innerValue * signs[2]),
      new THREE.Vector3(outerValue * signs[0], innerValue * signs[1], bridgeValue * signs[2]),
      new THREE.Vector3(innerValue * signs[0], outerValue * signs[1], bridgeValue * signs[2]),
      new THREE.Vector3(bridgeValue * signs[0], outerValue * signs[1], innerValue * signs[2]),
    ];
  }

  /**
   * 应用角点位置与朝向。
   *
   * @param position 角点中心位置。
   * @param quaternion 角点朝向。
   */
  private applyCornerPlacement(position: THREE.Vector3, quaternion: THREE.Quaternion): void {
    this.position.copy(position);
    this.quaternion.copy(quaternion);
  }

  /**
   * 解析角点对应的符号方向。
   *
   * @param viewType 标准视图类型。
   * @returns 三轴符号。
   */
  private static resolveCornerSigns(
    viewType: StandardViewType,
  ): readonly [number, number, number] {
    switch (viewType) {
      case StandardViewType.FrontTopRight:
        return [1, 1, 1];
      case StandardViewType.FrontTopLeft:
        return [-1, 1, 1];
      case StandardViewType.FrontBottomRight:
        return [1, -1, 1];
      case StandardViewType.FrontBottomLeft:
        return [-1, -1, 1];
      case StandardViewType.BackTopRight:
        return [1, 1, -1];
      case StandardViewType.BackTopLeft:
        return [-1, 1, -1];
      case StandardViewType.BackBottomRight:
        return [1, -1, -1];
      case StandardViewType.BackBottomLeft:
        return [-1, -1, -1];
      default:
        throw new Error(`不支持的角点视图类型：${viewType}`);
    }
  }

}
